import React, { useCallback, useEffect, useState, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps, AREA_OF_INTEREST } from 'types';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import { DataTableBasicViewComponent } from 'components/DataTableBasicView';
import { useComponentMount } from 'utilities/hooks.util';
import { toLocalDateTimeFormat } from 'utilities/helper.util';
import { DataTableSkeleton } from 'carbon-components-react';
import { addAlert, removeAlertByGroup } from 'redux/alerts/actions';
import {
  getAOIListStart,
  setLastVisitListPage
} from 'redux/areaOfInterest/actions';
import {
  queryFeatureLayerExtentStart,
  clearFeatureLayerLayerExtent
} from 'redux/map/actions';
import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import {
  zoomToExtent,
  removeSelectedAoiFeatureLayer,
  addOrRefreshSelectedAoiFeatureLayer
} from '../../drawingFunction';
import CustomPagination from './pagination';
import { isSafari } from 'react-device-detect';

const {
  modules,
  currentAOIDarkSymbol,
  currentAOIlightSymbol,
  previewAOIDarkSymbol,
  previewAOIlightSymbol
} = MapSettings;

const getRowClassName = (row: any) => {
  const cells = row?.cells && Array.isArray(row.cells) ? row.cells : [];
  return cells.length > 2 && cells[1]?.value === 'Current'
    ? 'aoi-history-list--current-row'
    : '';
};

export const AoiHistory: React.FC<Props> = ({
  organisationId,
  aoiLayerExtent,
  aoiList,
  errMsg,
  loadingState,
  lastVisitPage,
  baseMapTheme
}) => {
  const [mapReady, setMapReady] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string>('');
  const [currentAoiId, setCurrentAoiId] = useState<number>(0);
  const [rowData, setRowData] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number | undefined>();
  const extraMapModules = [modules.webMercatorUtils, modules.featureLayer];
  const dispatch = useDispatch();
  const baseMapThemeRef = useRef(baseMapTheme);
  const alertGroup = 'map-alert';
  const headerData = [
    { key: 'createdAt', header: 'Used from' },
    { key: 'deletedAt', header: 'Used until' },
    { key: 'aoiBuffer', header: 'Buffer distance' }
  ];

  const onMapReady = () => {
    const { loadedMapModules, loadedMap, loadedView } = window;
    const { Config, FeatureLayer } = loadedMapModules;
    Config.request.interceptors.push({
      urls: getFeatureServerUrl('AreasOfInterest'),
      before: featureRequestInterceptors
    });

    const currentFeatureLayer = new FeatureLayer({
      id: 'current-aoi-layer',
      url: getFeatureServerUrl('AreasOfInterest'),
      outFields: ['objectid'],
      definitionExpression: 'deleted_at IS NULL',
      renderer: {
        type: 'simple',
        symbol:
          baseMapTheme === 'dark' ? currentAOIDarkSymbol : currentAOIlightSymbol
      }
    });

    loadedMap.add(currentFeatureLayer, 0);

    loadedView.navigation.mouseWheelZoomEnabled = true;
    loadedView.navigation.momentumEnabled = true;
    loadedView.navigation.browserTouchPanEnabled = true;
    loadedView.navigation.gamepad.enabled = true;

    setMapReady(true);
  };

  const selectedRowHandler = useCallback(
    ({ id }: any) => {
      if (id !== currentAoiId.toString()) {
        if (id === selectedRowId) {
          setSelectedRowId('');
        } else {
          setSelectedRowId(id);
        }
      }
    },
    [currentAoiId, selectedRowId]
  );

  useEffect(() => {
    if (aoiList?.AreasOfInterest && Array.isArray(aoiList.AreasOfInterest)) {
      const rowData = aoiList.AreasOfInterest.map((item: any) => {
        !item.deletedAt && item?.id && setCurrentAoiId(item.id);
        return {
          id: String(item.id),
          createdAt: toLocalDateTimeFormat(item.createdAt),
          deletedAt: item.deletedAt
            ? toLocalDateTimeFormat(item.deletedAt)
            : 'Current',
          aoiBuffer: `${item.aoiBuffer || 25} m`
        };
      });
      setRowData(rowData);
      aoiList?.Info?.count && setTotalItems(aoiList.Info.count);
    }
  }, [aoiList]);

  useEffect(() => {
    currentPage &&
      dispatch(
        getAOIListStart({
          organisationId,
          params: {
            limit: AREA_OF_INTEREST.LIST_PAGE_SIZE,
            offset: AREA_OF_INTEREST.LIST_PAGE_SIZE * (currentPage - 1)
          }
        })
      );
    return () => {
      currentPage && dispatch(setLastVisitListPage(currentPage));
    };
  }, [currentPage, organisationId, dispatch]);

  useEffect(() => {
    loadingState === 'fail' &&
      typeof errMsg === 'string' &&
      errMsg &&
      dispatch(addAlert(alertGroup, 'error', 'Error', errMsg));
  }, [loadingState, errMsg, dispatch]);

  useEffect(() => {
    if (aoiLayerExtent) {
      if (typeof aoiLayerExtent !== 'string') {
        if (!aoiLayerExtent.noRecord) {
          zoomToExtent(aoiLayerExtent);
        }
      }
    }
  }, [aoiLayerExtent, dispatch]);

  useEffect(() => {
    if (mapReady) {
      if (selectedRowId !== '') {
        const layer = addOrRefreshSelectedAoiFeatureLayer(
          selectedRowId,
          baseMapThemeRef.current === 'dark'
        );
        dispatch(queryFeatureLayerExtentStart(layer));
      } else {
        removeSelectedAoiFeatureLayer();
        const layer = window.loadedMap.findLayerById('current-aoi-layer');
        dispatch(queryFeatureLayerExtentStart(layer));
      }
    }
  }, [selectedRowId, mapReady, dispatch]);

  useEffect(() => {
    if (mapReady) {
      const currentLayer = window.loadedMap.findLayerById('current-aoi-layer');
      if (currentLayer) {
        currentLayer.renderer = {
          type: 'simple',
          symbol:
            baseMapTheme === 'dark'
              ? currentAOIDarkSymbol
              : currentAOIlightSymbol
        };
      }
      const selectedLayer = window.loadedMap.findLayerById(
        'selected-aoi-layer'
      );
      if (selectedLayer) {
        selectedLayer.renderer = {
          type: 'simple',
          symbol:
            baseMapTheme === 'dark'
              ? previewAOIDarkSymbol
              : previewAOIlightSymbol
        };
      }
    }
  }, [baseMapTheme, mapReady]);

  useEffect(() => {
    if (mapReady) {
      const currentLayer = window.loadedMap.findLayerById('current-aoi-layer');
      if (currentLayer && organisationId) {
        currentLayer.definitionExpression = `deleted_at IS NULL AND organisation_id = ${organisationId}`;
        dispatch(queryFeatureLayerExtentStart(currentLayer));
      }
    }
  }, [organisationId, mapReady, dispatch]);

  useComponentMount(() => {
    lastVisitPage && setCurrentPage(lastVisitPage);
    return () => {
      dispatch(removeAlertByGroup(alertGroup));
      dispatch(clearFeatureLayerLayerExtent());
    };
  });

  return (
    <div className={`aoi-history-container${isSafari ? ' is-safari' : ''}`}>
      <div className="aoi-history-map--view">
        <Map
          showbaseMapButton={true}
          showCurrentLocationButton={false}
          onReady={onMapReady}
          extraModules={extraMapModules}
        />
      </div>
      <div className="aoi-history-list--container">
        <div id="aoi-history-list--table" className="aoi-history-list--table">
          {loadingState === 'processing' || totalItems === 0 ? (
            <DataTableSkeleton
              showToolbar={false}
              showHeader={false}
              columnCount={3}
              rowCount={1}
            />
          ) : (
            <DataTableBasicViewComponent
              isLoading={false}
              headerData={headerData}
              rowData={rowData}
              size="short"
              title={''}
              description={''}
              selectedRowId={selectedRowId}
              selectedRowHandler={selectedRowHandler}
              rowClassName={getRowClassName}
            />
          )}
        </div>
        <div
          id="aoi-history-list--pagination"
          className="aoi-history-list--pagination"
        >
          <CustomPagination
            page={currentPage || 1}
            baseMapTheme={baseMapTheme}
            pageSize={AREA_OF_INTEREST.LIST_PAGE_SIZE}
            totalItems={totalItems}
            onPageChange={(page: number) => setCurrentPage(page)}
          />
        </div>
      </div>
    </div>
  );
};

type ownProps = {
  organisationId: string;
};

const mapStateToProps = ({ aoiCache, mapCache, app }: RootState) => ({
  aoiLayerExtent: mapCache.featureLayerExtent,
  aoiList: aoiCache.aoiList,
  errMsg: aoiCache.errMsg,
  loadingState: aoiCache.loadingState,
  lastVisitPage: aoiCache.lastVisitListPage,
  baseMapTheme: app.baseMapTheme
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector> & ownProps;

AoiHistory.displayName = 'AreaOfInterestHistory';

export default React.memo(connector(AoiHistory));
